import React, { useState, useEffect, useCallback } from 'react';
import {
  FaUser, FaLock, FaUsers, FaSave, FaUserPlus, FaEnvelope,
  FaCheckCircle, FaClock,
} from 'react-icons/fa';
import SkeletonLoader from '../../components/ui/SkeletonLoader';
import PageLayout from '../../components/ui/PageLayout';
import PageHeader from '../../components/ui/PageHeader';
import { useToast } from '../../context/ToastContext';
import { useEmployee } from '../../context/EmployeeContext';

const TABS = [
  { id: 'profile', label: 'My Profile', icon: FaUser },
  { id: 'security', label: 'Security', icon: FaLock },
  { id: 'team', label: 'Create Employee', icon: FaUsers },
];

const EmployeeProfile = () => {
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  const [limits, setLimits] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [profileForm, setProfileForm] = useState({ name: '', email: '', profileImage: '' });
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [createForm, setCreateForm] = useState({ name: '', email: '', password: '' });
  const toast = useToast();
  const { refreshEmployee } = useEmployee();

  const API = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';
  const token = () => localStorage.getItem('employeeToken');
  const headers = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token()}`,
  });

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/employee/profile/me`, { headers: headers() });
      if (!res.ok) throw new Error('Failed to load profile');
      const data = await res.json();
      setProfile(data.profile);
      setLimits(data.limits);
      setProfileForm({
        name: data.profile?.name || '',
        email: data.profile?.email || '',
        profileImage: data.profile?.profileImage || '',
      });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [API, toast]);

  const fetchTeamMembers = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/employee/all`, { headers: headers() });
      if (!res.ok) throw new Error('Failed to load team members');
      const data = await res.json();
      setTeamMembers(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err.message);
    }
  }, [API, toast]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  useEffect(() => {
    if (activeTab === 'team') fetchTeamMembers();
  }, [activeTab, fetchTeamMembers]);

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/employee/profile/me`, {
        method: 'PUT',
        headers: headers(),
        body: JSON.stringify(profileForm),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Update failed');
      setProfile(data.profile);
      await refreshEmployee();
      toast.success('Profile updated successfully');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/employee/profile/change-password`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({
          currentPassword: passwordForm.currentPassword,
          newPassword: passwordForm.newPassword,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Password change failed');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      toast.success('Password changed successfully');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCreateEmployee = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/employee/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(createForm),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to create employee');
      setCreateForm({ name: '', email: '', password: '' });
      fetchTeamMembers();
      toast.success(data.message || 'Employee created! Verification email sent.');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <SkeletonLoader variant="page" />;

  const initials = (profile?.name || profile?.email || 'E')[0]?.toUpperCase();
  const clientCount = profile?.clients?.length ?? profile?.clientsCount ?? 0;

  return (
    <PageLayout>
      <PageHeader
        title="My Profile"
        description="Manage your personal profile, security settings, and create new employee accounts"
        icon={FaUser}
        badge={
          limits && (
            <span className="inline-flex items-center px-4 py-2 rounded-xl bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 text-sm font-medium border border-primary-100 dark:border-primary-800">
              {limits.totalEmployees} Team Member{limits.totalEmployees !== 1 ? 's' : ''}
            </span>
          )
        }
      />

      <div className="flex flex-wrap gap-2 mb-6">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all border ${
                isActive
                  ? 'btn-premium !py-2.5 border-transparent'
                  : 'bg-white dark:bg-neutral-900 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-neutral-700 hover:bg-gray-50 dark:hover:bg-neutral-800'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'profile' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1">
            <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-gray-100 dark:border-neutral-800 p-6 text-center shadow-card">
              <div className="w-24 h-24 mx-auto rounded-2xl bg-gradient-to-br from-primary-500 to-primary-700 text-white flex items-center justify-center text-3xl font-bold overflow-hidden">
                {profileForm.profileImage ? (
                  <img src={profileForm.profileImage} alt={profile?.name} className="w-full h-full object-cover" />
                ) : initials}
              </div>
              <h3 className="mt-4 text-lg font-semibold text-gray-900 dark:text-white">
                {profile?.name || 'Employee'}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">{profile?.email}</p>
              <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
                {profile?.isVerified ? (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 text-xs font-medium">
                    <FaCheckCircle className="w-3 h-3" /> Verified
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 text-xs font-medium">
                    <FaClock className="w-3 h-3" /> Pending Verification
                  </span>
                )}
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 text-xs font-medium">
                  {clientCount} Client{clientCount !== 1 ? 's' : ''}
                </span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-2">
            <form onSubmit={handleProfileSave} className="bg-white dark:bg-neutral-900 rounded-2xl border border-gray-100 dark:border-neutral-800 p-6 shadow-card space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Personal Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase">Display Name</label>
                  <div className="relative mt-1">
                    <FaUser className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      className="input-modern pl-10"
                      value={profileForm.name}
                      onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                      placeholder="Your name"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase">Email (Login)</label>
                  <div className="relative mt-1">
                    <FaEnvelope className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="email"
                      className="input-modern pl-10"
                      value={profileForm.email}
                      onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                      placeholder="employee@example.com"
                      required
                    />
                  </div>
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs font-semibold text-gray-500 uppercase">Profile Image URL</label>
                  <input
                    className="input-modern mt-1"
                    value={profileForm.profileImage}
                    onChange={(e) => setProfileForm({ ...profileForm, profileImage: e.target.value })}
                    placeholder="https://..."
                  />
                </div>
              </div>
              <button type="submit" disabled={saving} className="btn-premium inline-flex items-center gap-2 !py-2.5">
                <FaSave className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save Profile'}
              </button>
            </form>
          </div>
        </div>
      )}

      {activeTab === 'security' && (
        <div className="max-w-xl">
          <form onSubmit={handlePasswordChange} className="bg-white dark:bg-neutral-900 rounded-2xl border border-gray-100 dark:border-neutral-800 p-6 shadow-card space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <FaLock className="text-primary-500" /> Change Password
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Enter your current password to update immediately. No email approval required while logged in.
            </p>
            <input
              type="password"
              className="input-modern"
              placeholder="Current Password"
              value={passwordForm.currentPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
              required
            />
            <input
              type="password"
              className="input-modern"
              placeholder="New Password"
              value={passwordForm.newPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
              required
            />
            <input
              type="password"
              className="input-modern"
              placeholder="Confirm New Password"
              value={passwordForm.confirmPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
              required
            />
            <button type="submit" disabled={saving} className="btn-premium w-full !py-2.5">
              {saving ? 'Updating...' : 'Update Password'}
            </button>
          </form>
        </div>
      )}

      {activeTab === 'team' && (
        <div className="space-y-6">
          <form onSubmit={handleCreateEmployee} className="bg-white dark:bg-neutral-900 rounded-2xl border border-gray-100 dark:border-neutral-800 p-6 shadow-card space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <FaUserPlus className="text-primary-500" /> Create Employee Account
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Register a new team member. They will receive a verification email to activate their account.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                className="input-modern"
                placeholder="Full Name"
                value={createForm.name}
                onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                required
              />
              <input
                type="email"
                className="input-modern"
                placeholder="Email Address"
                value={createForm.email}
                onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                required
              />
              <input
                type="password"
                className="input-modern md:col-span-2"
                placeholder="Temporary Password (min 6 characters)"
                value={createForm.password}
                onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                required
                minLength={6}
              />
            </div>
            <button type="submit" disabled={saving} className="btn-premium inline-flex items-center gap-2 !py-2.5">
              <FaUserPlus className="w-4 h-4" />
              {saving ? 'Creating...' : 'Create Employee'}
            </button>
          </form>

          <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-gray-100 dark:border-neutral-800 p-6 shadow-card">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Team Members</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 dark:border-neutral-800 text-left text-xs uppercase text-gray-500">
                    <th className="py-2 pr-3">Employee</th>
                    <th className="py-2 pr-3">Email</th>
                    <th className="py-2 pr-3">Clients</th>
                    <th className="py-2">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-neutral-800">
                  {teamMembers.map((member) => (
                    <tr key={member._id}>
                      <td className="py-3 pr-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-500 to-secondary-500 text-white flex items-center justify-center text-xs font-bold shrink-0">
                            {member.name?.[0]?.toUpperCase() || 'E'}
                          </div>
                          <span className="font-medium text-gray-900 dark:text-white">{member.name}</span>
                        </div>
                      </td>
                      <td className="py-3 pr-3 text-gray-600 dark:text-gray-400">{member.email}</td>
                      <td className="py-3 pr-3">{member.clients?.length ?? 0}</td>
                      <td className="py-3">
                        {member.isVerified ? (
                          <span className="text-emerald-600 dark:text-emerald-400 text-xs font-medium">Verified</span>
                        ) : (
                          <span className="text-amber-600 dark:text-amber-400 text-xs font-medium">Pending</span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {teamMembers.length === 0 && (
                    <tr>
                      <td colSpan={4} className="py-8 text-center text-gray-500 dark:text-gray-400">
                        No team members found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
};

export default EmployeeProfile;
